/**
 * Status / navigation bar tint — the ONLY module in the app that touches
 * Capacitor's built-in `SystemBars` plugin.
 *
 * `SystemBars` ships inside `@capacitor/core` 8 (no extra plugin to install)
 * and exists so a full-screen dark surface — the file viewer — can ask the OS
 * for LIGHT status-bar icons while it is up, and hand the decision back when it
 * closes. Two honest limits: it tints the ICONS only (edge-to-edge Android has
 * no API for the bar's background colour; the page shows through), and its web
 * implementation throws `unavailable`, so every call is native-guarded and
 * never throws — a viewer must open whether or not the bar cooperates.
 *
 * Semantic trap worth restating: `SystemBarsStyle.Dark` means *light content on
 * a dark background* — that is what an always-dark viewer wants.
 */
import { Capacitor, SystemBars, SystemBarsStyle } from '@capacitor/core';
import { IS_DEV } from '@/utils/env';

function isSupported(): boolean {
  return Capacitor.isNativePlatform() && Capacitor.isPluginAvailable('SystemBars');
}

async function setStyle(style: SystemBarsStyle): Promise<void> {
  if (!isSupported()) return;
  try {
    await SystemBars.setStyle({ style });
  } catch (err) {
    if (IS_DEV) console.warn('[system-bars] setStyle', err);
  }
}

/** Light icons on a dark background — for an always-dark full-screen surface. */
export function useDarkSurfaceBars(): Promise<void> {
  return setStyle(SystemBarsStyle.Dark);
}

/** Back to following the device / app appearance. */
export function restoreDefaultBars(): Promise<void> {
  return setStyle(SystemBarsStyle.Default);
}
